// Date-range presets — one definition for every screen with a "بازه" filter
// (Accounting, Encounters). Windows are { fromTs, toTs } in epoch MS, because
// invoice/encounter created_at is stored in ms and a seconds value would silently
// match nothing.
//
// «این ماه»/«ماه گذشته» are Jalali months, not Gregorian ones — no Date arithmetic
// can find the first of Tir, so month bounds go through the Jalali calendar.
// A brand that reads the Gregorian calendar (`calendar: 'gregory'`,
// docs/rebranding/CONTRACT.md) has Gregorian months instead, counted the same way:
// from the first of the month at 00:00 UTC, the instant `jalaliToMs` returns.
import { msToJalali, jalaliToMs } from '@parsina/framework/clinical/src/lib/jalali';
import { calendarSystem } from '@parsina/framework/ui/lib/brand';

const DAY_MS = 86400000;
const startOfDay = (item) => { const d = new Date(item); d.setHours(0, 0, 0, 0);      return d.getTime(); };
const endOfDay   = (item) => { const d = new Date(item); d.setHours(23, 59, 59, 999); return d.getTime(); };
const gStrStart  = (s) => { const [y, m, d] = s.split('-').map(Number); return new Date(y, m - 1, d, 0, 0, 0, 0).getTime(); };
const gStrEnd    = (s) => { const [y, m, d] = s.split('-').map(Number); return new Date(y, m - 1, d, 23, 59, 59, 999).getTime(); };

export const RANGE_PRESETS = [
  { id: 'today',     labelKey: 'common.range.today' },
  { id: 'week',      labelKey: 'common.range.week' },
  { id: 'month',     labelKey: 'common.range.month' },
  { id: 'lastMonth', labelKey: 'common.range.lastMonth' },
  { id: 'quarter',   labelKey: 'common.range.quarter' },
  { id: 'all',       labelKey: 'common.range.all' },
];

/**
 * The presets in the shape `@parsina/framework`'s DateRangeFilter wants.
 *
 * That component takes `{ id, label }` and is shared with the clinic and
 * patient apps, so it cannot be taught about translation keys. Adapting here
 * keeps the registry key-based without the doctor app reaching into a package
 * two other apps depend on.
 */
export const translatedPresets = (item) =>
  RANGE_PRESETS.map((p) => ({ ...p, label: item(p.labelKey) }));

/** The first instant of the Gregorian month `offset` months from `now`'s (UTC). */
const gregorianMonthStart = (now, offset = 0) => {
  const d = new Date(now);
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + offset, 1);
};

/** { fromTs, toTs } window for a preset id, or null for the unbounded 'all'. */
export function presetRange(id, now = Date.now()) {
  if (id === 'today') return { fromTs: startOfDay(now), toTs: endOfDay(now) };
  if (id === 'week')  return { fromTs: startOfDay(now - 6 * DAY_MS), toTs: endOfDay(now) };
  if (calendarSystem('jalali') === 'gregory') {
    if (id === 'month') return { fromTs: gregorianMonthStart(now), toTs: endOfDay(now) };
    if (id === 'lastMonth') return { fromTs: gregorianMonthStart(now, -1), toTs: gregorianMonthStart(now) - 1 };
  }
  if (id === 'month') { const jt = msToJalali(now); return { fromTs: jalaliToMs(jt.y, jt.m, 1), toTs: endOfDay(now) }; }
  if (id === 'lastMonth') {
    const jt = msToJalali(now);
    const pm = jt.m === 1 ? { y: jt.y - 1, m: 12 } : { y: jt.y, m: jt.m - 1 };
    // …to one ms before this month starts, so the two month presets never overlap.
    return { fromTs: jalaliToMs(pm.y, pm.m, 1), toTs: jalaliToMs(jt.y, jt.m, 1) - 1 };
  }
  if (id === 'quarter') return { fromTs: startOfDay(now - 89 * DAY_MS), toTs: endOfDay(now) };
  return null; // 'all' — unbounded
}

/** { fromTs, toTs } window from two 'YYYY-MM-DD' Gregorian strings. */
export const customRange = (from, to) => ({ fromTs: gStrStart(from), toTs: gStrEnd(to) });
